
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Sidebar from "@/components/layout/Sidebar";
import { useAuth } from "@/context/auth/AuthContext";
import { updateUserProfile } from "@/services/auth.service";
import { useToast } from "@/hooks/use-toast";
import { useThemeConfig } from "@/components/ui/theme-config-provider";

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme } = useThemeConfig();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdateProfile = async () => {
    if (!name.trim()) {
      toast({
        title: "Errore",
        description: "Il nome non può essere vuoto",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const success = await updateUserProfile(name);
      
      if (success) {
        toast({
          description: "Profilo aggiornato con successo",
          variant: "success"
        });
        refreshUser();
        setIsEditing(false);
      } else {
        toast({
          title: "Errore",
          description: "Impossibile aggiornare il profilo",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Errore",
        description: "Si è verificato un errore durante l'aggiornamento del profilo",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-viewport-height overflow-y-auto pb-safe" style={{
      backgroundColor: theme.backgroundColor || '#f1f0fb',
      color: theme.textColor || '#000000'
    }}>
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="container max-w-2xl py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <h1
            className="text-2xl font-bold"
            style={{ color: theme.sectionTitleColor }}
          >
            Il mio profilo
          </h1>
        </div>
        
        <Card
          className="mt-6"
          style={{
            backgroundColor: theme.cardBackgroundColor,
            borderRadius: theme.cardBorderRadius,
            boxShadow: theme.cardShadow,
          }}
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-xl" style={{ color: theme.sectionTitleColor }}>
              Informazioni personali
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1" style={{ color: theme.sectionTextColor }}>
                    Nome
                  </label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full"
                    disabled={isLoading}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button
                    onClick={handleUpdateProfile}
                    disabled={isLoading}
                    style={{
                      backgroundColor: theme.primaryColor,
                      borderRadius: theme.buttonBorderRadius,
                      padding: theme.buttonPadding,
                      color: theme.textPrimaryColor,
                      fontWeight: 'bold',
                      transitionDuration: theme.transitionSpeed,
                    }}
                  >
                    {isLoading ? 'Salvataggio...' : 'Salva modifiche'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setName(user?.name || '');
                      setIsEditing(false);
                    }}
                    disabled={isLoading}
                    style={{
                      backgroundColor: theme.backgroundColor,
                      color: theme.textColor,
                      borderRadius: theme.buttonBorderRadius,
                      padding: theme.buttonPadding,
                      fontWeight: theme.buttonFontWeight,
                      transitionDuration: theme.transitionSpeed,
                      '--btn-bg-hover': theme.primaryColor,
                    } as React.CSSProperties}
                  >
                    Annulla
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="block text-sm font-medium" style={{ color: theme.sectionTextColor }}>Nome</span>
                    <span className="mt-1 block text-lg">{user?.name || 'Non impostato'}</span>
                  </div>
                  <div>
                    <span className="block text-sm font-medium" style={{ color: theme.sectionTextColor }}>Email</span>
                    <span className="mt-1 block text-lg">{user?.email || 'Non impostata'}</span>
                  </div>
                </div>
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="outline"
                  style={{
                    borderRadius: theme.buttonBorderRadius,
                    padding: theme.buttonPadding,
                    transitionDuration: theme.transitionSpeed,
                    backgroundColor: theme.primaryColor,
                    color: theme.textPrimaryColor,
                  }}
                >
                  Modifica profilo
                </Button>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card
          className="mt-6"
          style={{
            backgroundColor: theme.cardBackgroundColor,
            borderRadius: theme.cardBorderRadius,
            boxShadow: theme.cardShadow,
          }}
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-xl" style={{ color: theme.sectionTitleColor }}>
              Account
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="mb-4">
              <span className="block text-sm font-medium" style={{ color: theme.sectionTextColor }}>Piano</span>
              <span className="mt-1 block text-lg">
                {user?.isPremium ? 'Premium 👑' : 'Gratuito'}
              </span>
            </div>
            <Button
              onClick={() => navigate('/settings')}
              variant="outline"
              className="mt-3"
              style={{
                borderRadius: theme.buttonBorderRadius,
                padding: theme.buttonPadding,
                transitionDuration: theme.transitionSpeed,
                backgroundColor: theme.primaryColor,
                color: theme.textPrimaryColor,
              }}
            >
              Impostazioni account
            </Button>
            {!user?.isPremium && (
              <Button
                onClick={() => navigate('/premium')}
                className="mt-3 ml-2"
                style={{
                  backgroundColor: theme.primaryColor,
                  borderRadius: theme.buttonBorderRadius,
                  padding: theme.buttonPadding,
                  color: theme.textPrimaryColor,
                  fontWeight: 'bold',
                  transitionDuration: theme.transitionSpeed,
                }}
              >
                Passa a Premium
              </Button>
            )}
          </CardContent>
        </Card>

        <Card
          className="mt-6"
          style={{
            backgroundColor: theme.cardBackgroundColor,
            borderRadius: theme.cardBorderRadius,
            boxShadow: theme.cardShadow,
          }}
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-xl" style={{ color: theme.sectionTitleColor }}>
              Dispositivi
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="mb-4" style={{ color: theme.sectionTextColor }}>
              Gestisci i dispositivi autorizzati al tuo account.
            </p>
            <Button
              onClick={() => navigate('/devices')}
              variant="outline"
              style={{
                borderRadius: theme.buttonBorderRadius,
                padding: theme.buttonPadding,
                transitionDuration: theme.transitionSpeed,
                backgroundColor: theme.primaryColor,
                color: theme.textPrimaryColor,
              }}
            >
              Gestisci dispositivi
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProfilePage;
